import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/contexts/auth-context';
import { DashboardLayout } from '@/components/layout/dashboard-layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/hooks/use-toast';
import { ArrowLeft, Loader2, Plus, Video, FileText, Paperclip, Trash2, Save, GripVertical, Edit } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Course {
  id: string;
  title: string;
  description: string;
  price: number;
  level: string;
  language: string;
  max_students: number | null;
  category_id: string;
}

interface Lesson {
  id: string;
  title: string;
  description: string | null;
  content: string | null;
  video_url: string | null;
  duration: number;
  order_index: number;
  content_type: string;
}

interface Material {
  id: string;
  name: string;
  file_url: string;
  file_type: string;
  file_size: number;
}

export default function EditCoursePage() {
  const { profile } = useAuth();
  const navigate = useNavigate();
  const params = useParams();
  const { toast } = useToast();
  const courseId = params.id as string;

  const [course, setCourse] = useState<Course | null>(null);
  const [lessons, setLessons] = useState<Lesson[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [showLessonDialog, setShowLessonDialog] = useState(false);
  const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);
  const [lessonForm, setLessonForm] = useState({
    title: '',
    description: '',
    content: '',
    video_url: '',
    content_type: 'mixed',
  });

  useEffect(() => {
    if (profile) {
      fetchCourseData();
    }
  }, [profile, courseId]);

  const fetchCourseData = async () => {
    setLoading(true);

    const [courseRes, lessonsRes, categoriesRes] = await Promise.all([
      supabase.from('courses').select('*').eq('id', courseId).eq('teacher_id', profile?.id).maybeSingle(),
      supabase.from('lessons').select('*').eq('course_id', courseId).order('order_index', { ascending: true }),
      supabase.from('categories').select('*').order('name', { ascending: true }),
    ]);

    if (courseRes.data) {
      setCourse(courseRes.data);
    } else {
      toast({
        title: 'Errore',
        description: 'Corso non trovato o non autorizzato',
        variant: 'destructive',
      });
      navigate('/dashboard/my-courses');
    }

    if (lessonsRes.data) {
      setLessons(lessonsRes.data);
    }

    if (categoriesRes.data) {
      setCategories(categoriesRes.data);
    }

    setLoading(false);
  };

  const handleCourseUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    const { error } = await supabase
      .from('courses')
      .update(course!)
      .eq('id', courseId);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile salvare le modifiche',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Salvato',
        description: 'Modifiche salvate con successo',
      });
    }

    setSaving(false);
  };

  const handleAddLesson = () => {
    setEditingLesson(null);
    setLessonForm({
      title: '',
      description: '',
      content: '',
      video_url: '',
      content_type: 'mixed',
    });
    setShowLessonDialog(true);
  };

  const handleEditLesson = (lesson: Lesson) => {
    setEditingLesson(lesson);
    setLessonForm({
      title: lesson.title,
      description: lesson.description || '',
      content: lesson.content || '',
      video_url: lesson.video_url || '',
      content_type: lesson.content_type || 'mixed',
    });
    setShowLessonDialog(true);
  };

  const handleSaveLesson = async () => {
    if (!lessonForm.title.trim()) {
      toast({
        title: 'Errore',
        description: 'Il titolo della lezione è obbligatorio',
        variant: 'destructive',
      });
      return;
    }

    setSaving(true);

    if (editingLesson) {
      const { error } = await supabase
        .from('lessons')
        .update({
          title: lessonForm.title,
          description: lessonForm.description,
          content: lessonForm.content,
          video_url: lessonForm.video_url,
          content_type: lessonForm.content_type,
        })
        .eq('id', editingLesson.id);

      if (error) {
        toast({
          title: 'Errore',
          description: 'Impossibile modificare la lezione',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Successo',
          description: 'Lezione modificata con successo',
        });
        fetchCourseData();
        setShowLessonDialog(false);
      }
    } else {
      const { error } = await supabase
        .from('lessons')
        .insert({
          course_id: courseId,
          title: lessonForm.title,
          description: lessonForm.description,
          content: lessonForm.content,
          video_url: lessonForm.video_url,
          content_type: lessonForm.content_type,
          order_index: lessons.length,
        });

      if (error) {
        toast({
          title: 'Errore',
          description: 'Impossibile aggiungere la lezione',
          variant: 'destructive',
        });
      } else {
        toast({
          title: 'Successo',
          description: 'Lezione aggiunta con successo',
        });
        fetchCourseData();
        setShowLessonDialog(false);
      }
    }

    setSaving(false);
  };

  const handleDeleteLesson = async (lessonId: string) => {
    if (!confirm('Sei sicuro di voler eliminare questa lezione?')) return;

    const { error } = await supabase.from('lessons').delete().eq('id', lessonId);

    if (error) {
      toast({
        title: 'Errore',
        description: 'Impossibile eliminare la lezione',
        variant: 'destructive',
      });
    } else {
      toast({
        title: 'Successo',
        description: 'Lezione eliminata',
      });
      fetchCourseData();
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!course) {
    return (
      <DashboardLayout>
        <Card>
          <CardContent className="py-12 text-center">
            <p className="text-gray-600">Corso non trovato</p>
            <Link to="/dashboard/my-courses">
              <Button className="mt-4">Torna ai miei corsi</Button>
            </Link>
          </CardContent>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-6 pb-8">
        <div>
          <Link to="/dashboard/my-courses">
            <Button variant="ghost" size="sm" className="mb-4">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Torna ai corsi
            </Button>
          </Link>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Modifica Corso
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            Gestisci contenuti e lezioni del corso
          </p>
        </div>

        <form onSubmit={handleCourseUpdate} className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Informazioni del corso</CardTitle>
              <CardDescription>Modifica le informazioni base del corso</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="title">Titolo</Label>
                  <Input
                    id="title"
                    value={course.title}
                    onChange={(e) => setCourse({ ...course, title: e.target.value })}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="price">Prezzo (€)</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={course.price}
                    onChange={(e) => setCourse({ ...course, price: parseFloat(e.target.value) })}
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Descrizione</Label>
                <Textarea
                  id="description"
                  rows={4}
                  value={course.description}
                  onChange={(e) => setCourse({ ...course, description: e.target.value })}
                  required
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Livello</Label>
                  <Select
                    value={course.level}
                    onValueChange={(value) => setCourse({ ...course, level: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Principiante">Principiante</SelectItem>
                      <SelectItem value="Intermedio">Intermedio</SelectItem>
                      <SelectItem value="Avanzato">Avanzato</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Lingua</Label>
                  <Select
                    value={course.language}
                    onValueChange={(value) => setCourse({ ...course, language: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Italiano">Italiano</SelectItem>
                      <SelectItem value="Inglese">Inglese</SelectItem>
                      <SelectItem value="Spagnolo">Spagnolo</SelectItem>
                      <SelectItem value="Francese">Francese</SelectItem>
                      <SelectItem value="Tedesco">Tedesco</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="max_students">Studenti massimi</Label>
                  <Input
                    id="max_students"
                    type="number"
                    min="1"
                    placeholder="Illimitato"
                    value={course.max_students || ''}
                    onChange={(e) => setCourse({ ...course, max_students: e.target.value ? parseInt(e.target.value) : null })}
                  />
                </div>
              </div>

              <Button type="submit" disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salva modifiche
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </form>

        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Lezioni del corso</CardTitle>
                <CardDescription>Gestisci il contenuto delle lezioni</CardDescription>
              </div>
              <Button onClick={handleAddLesson}>
                <Plus className="w-4 h-4 mr-2" />
                Aggiungi lezione
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {lessons.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  Nessuna lezione ancora
                </h3>
                <p className="text-gray-600 mb-4">
                  Inizia ad aggiungere lezioni al tuo corso
                </p>
                <Button onClick={handleAddLesson}>
                  <Plus className="w-4 h-4 mr-2" />
                  Aggiungi prima lezione
                </Button>
              </div>
            ) : (
              <Accordion type="single" collapsible className="space-y-2">
                {lessons.map((lesson, index) => (
                  <AccordionItem key={lesson.id} value={lesson.id} className="border rounded-lg px-4">
                    <AccordionTrigger className="hover:no-underline">
                      <div className="flex items-center gap-3 flex-1 text-left">
                        <GripVertical className="w-4 h-4 text-gray-400" />
                        <span className="font-semibold text-sm text-gray-500">#{index + 1}</span>
                        <span className="font-semibold">{lesson.title}</span>
                        <Badge variant="secondary" className="ml-auto mr-4">
                          {lesson.content_type === 'video' ? 'Video' : lesson.content_type === 'text' ? 'Testo' : 'Misto'}
                        </Badge>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-4 pb-2">
                      <div className="space-y-4">
                        {lesson.description && (
                          <div className="p-3 bg-gray-50 rounded-lg">
                            <p className="text-sm text-gray-700">{lesson.description}</p>
                          </div>
                        )}

                        {lesson.video_url && (
                          <div className="p-3 bg-blue-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <Video className="w-4 h-4 text-blue-600" />
                              <span className="text-sm font-semibold text-blue-900">Video URL</span>
                            </div>
                            <a
                              href={lesson.video_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-sm text-blue-600 hover:underline break-all"
                            >
                              {lesson.video_url}
                            </a>
                          </div>
                        )}

                        {lesson.content && (
                          <div className="p-3 bg-green-50 rounded-lg">
                            <div className="flex items-center gap-2 mb-2">
                              <FileText className="w-4 h-4 text-green-600" />
                              <span className="text-sm font-semibold text-green-900">Contenuto testuale</span>
                            </div>
                            <div className="text-sm text-gray-700 line-clamp-3">
                              {lesson.content}
                            </div>
                          </div>
                        )}

                        <div className="flex gap-2 pt-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEditLesson(lesson)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            Modifica
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDeleteLesson(lesson.id)}
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Elimina
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            )}
          </CardContent>
        </Card>

        <Dialog open={showLessonDialog} onOpenChange={setShowLessonDialog}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {editingLesson ? 'Modifica lezione' : 'Nuova lezione'}
              </DialogTitle>
              <DialogDescription>
                Inserisci il contenuto della lezione
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="lesson-title">Titolo lezione *</Label>
                <Input
                  id="lesson-title"
                  placeholder="Es: Introduzione al corso"
                  value={lessonForm.title}
                  onChange={(e) => setLessonForm({ ...lessonForm, title: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="lesson-description">Descrizione breve</Label>
                <Textarea
                  id="lesson-description"
                  placeholder="Breve descrizione della lezione..."
                  rows={2}
                  value={lessonForm.description}
                  onChange={(e) => setLessonForm({ ...lessonForm, description: e.target.value })}
                />
              </div>

              <div className="space-y-2">
                <Label>Tipo di contenuto</Label>
                <Select
                  value={lessonForm.content_type}
                  onValueChange={(value) => setLessonForm({ ...lessonForm, content_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="video">Solo Video</SelectItem>
                    <SelectItem value="text">Solo Testo</SelectItem>
                    <SelectItem value="mixed">Video + Testo</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {(lessonForm.content_type === 'video' || lessonForm.content_type === 'mixed') && (
                <div className="space-y-2">
                  <Label htmlFor="lesson-video">URL Video</Label>
                  <Input
                    id="lesson-video"
                    placeholder="https://youtube.com/watch?v=..."
                    value={lessonForm.video_url}
                    onChange={(e) => setLessonForm({ ...lessonForm, video_url: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    Supporta YouTube, Vimeo, o link diretti a file video
                  </p>
                </div>
              )}

              {(lessonForm.content_type === 'text' || lessonForm.content_type === 'mixed') && (
                <div className="space-y-2">
                  <Label htmlFor="lesson-content">Contenuto testuale</Label>
                  <Textarea
                    id="lesson-content"
                    placeholder="Scrivi il contenuto della lezione qui..."
                    rows={10}
                    value={lessonForm.content}
                    onChange={(e) => setLessonForm({ ...lessonForm, content: e.target.value })}
                  />
                  <p className="text-xs text-gray-500">
                    Puoi inserire testo formattato, liste, codice, ecc.
                  </p>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowLessonDialog(false)}>
                Annulla
              </Button>
              <Button onClick={handleSaveLesson} disabled={saving}>
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Salvataggio...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Salva lezione
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
